import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import type { MediaProvider } from '@atlaskit/editor-common/provider-factory/media-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import type { AnalyticsAttributes } from '@atlassian/projects-goals-common/analytics';
import { createPlatformSiteAri } from '@atlassian/projects-goals-common/create-platform-site-ari';
import {
	getGoalSiteAri,
	getProjectSiteAri,
} from '@atlassian/projects-goals-common/get-app-site-ari';
import { getSiteId } from '@atlassian/projects-goals-common/get-site-id';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, { type GoalPanelQuery } from './__generated__/GoalPanelQuery.graphql';
import type { ProductName, TabType } from './types';

type EntryPointParams = { ari: string };
const fetchPolicy: PreloadFetchPolicy = 'network-only';

type AtlasGoalEntryPointComponent = EntryPointComponent<
	{
		queryReference: GoalPanelQuery;
	},
	Record<string, never>,
	{
		parentProduct: ProductName;
		cloudId: string;
		analyticsAttributes?: AnalyticsAttributes;
		organizationId?: string;
		url?: string;
		defaultTab?: TabType;
		mediaProvider?: Promise<MediaProvider>;
	},
	Record<string, never>
>;

const atlasGoalEntryPoint: EntryPoint<AtlasGoalEntryPointComponent, EntryPointParams> =
	createEntryPoint({
		root: JSResourceForInteraction<AtlasGoalEntryPointComponent>(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/entry-point-fixtures/GoalPanel" */ './GoalPanel',
			{ moduleId: 'atlas-goal-entrypoint' },
		),
		getPreloadProps: ({ ari }: EntryPointParams) => {
			traceUFOPress('townsquare-goal-panel-opened');
			const includeDraftUpdate = fg('townsquare-ai-updates-drafts-on-agg');

			const siteId = getSiteId('goal', ari);
			const queryContext = siteId ? createPlatformSiteAri(siteId) : null;
			const capabilitiesContainerId = siteId ? getProjectSiteAri(siteId) : '';

			return {
				queries: {
					queryReference: {
						parameters,
						variables: {
							ari,
							includeDraftUpdate,
							appSettingsContainerId: siteId ? getGoalSiteAri(siteId) : '',
							queryContext,
							capabilitiesContainerId,
						},
						options: {
							fetchPolicy,
						},
					},
				},
			};
		},
	});

export default atlasGoalEntryPoint;
